import { useEffect, useMemo } from 'react';

import { useExportPreview } from '@/features/DataHub/modules/Export/context/exportContexts';
import { usePreviewData } from '@/features/DataHub/modules/Export/hooks';
import type { ExportOption, PreviewConfig } from '@/features/DataHub/modules/Export/types';
import { scrambleSecrets } from '@/features/DataHub/modules/Export/utils';

import ExportPreviewScreen from './ExportPreviewScreen';
import PreviewContent from './PreviewContent';

type UniversalPreviewRendererProps = {
  previewConfig: PreviewConfig | undefined;
  exportOption: ExportOption;
};

const UniversalPreviewRenderer = ({
  previewConfig,
  exportOption
}: UniversalPreviewRendererProps) => {
  const { setPreviewContentToCopy } = useExportPreview();
  const { loading, error, fileContent, downloadFile, content } = usePreviewData(
    exportOption,
    previewConfig
  );

  // Memoize scrambled content for display only; copy and download use raw content (unsanitized)
  const scrambledContent = useMemo(() => scrambleSecrets(content || ''), [content]);

  // Expose raw content for copy so "Copy to clipboard" gets unsanitized text (same as download)
  useEffect(() => {
    if (!loading && !error && content) {
      setPreviewContentToCopy(content);
    }
    return () => {
      setPreviewContentToCopy(null);
    };
  }, [loading, error, content, setPreviewContentToCopy]);

  return (
    <ExportPreviewScreen
      previewMeta={{
        showTokenLink: exportOption.showTokenLink,
        bulletPoints: exportOption.bulletPoints,
        documentationType: exportOption.documentationType
      }}
    >
      <PreviewContent
        downloadFile={downloadFile}
        error={error}
        fileContent={fileContent}
        loading={loading}
        previewConfig={previewConfig}
        scrambledContent={scrambledContent}
      />
    </ExportPreviewScreen>
  );
};

export default UniversalPreviewRenderer;
